'use client';

import {useEffect} from 'react';

import {SITE_TITLE} from '@/lib/utils/contants/siteMetadata';

const STANDARD_TITLE = SITE_TITLE;

// Gap appended before the title loops back around so the marquee reads as a
// continuous scroll instead of the text abruptly jumping. Non-breaking spaces
// keep browsers from collapsing them in the tab.
const MARQUEE_GAP = '  •  ';
const MARQUEE_TEXT = STANDARD_TITLE + MARQUEE_GAP;
const MARQUEE_STEP_MS = 400;

const ALT_TITLES = [
    'Come on, Aldex',
    "Don't hesitate — act",
    "Don't wait — jump into the game!",
    'Hey, where did you go?',
];

const isDev = process.env.NEXT_PUBLIC_APP_ENV === 'dev';
const STANDARD_FAVICON = isDev ? '/favicon-dev.ico' : '/favicon-prod.ico';

const HEARTBEAT_FRAMES = [1.0, 0.5, 1.0];

function getFaviconEl(): HTMLLinkElement | null {
    return document.querySelector("link[rel~='icon']");
}

export default function TabActivityManager() {
    useEffect(() => {
        let titleIntervalId: ReturnType<typeof setInterval> | null = null;
        let faviconIntervalId: ReturnType<typeof setInterval> | null = null;
        let marqueeIntervalId: ReturnType<typeof setInterval> | null = null;
        let frameIndex = 0;

        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');

        const img = new Image();
        let imgReady = false;
        let originalHref: string | null = null;

        function loadFaviconImage() {
            const faviconEl = getFaviconEl();
            if (!faviconEl) return;
            originalHref = faviconEl.href;
            img.onload = () => {
                imgReady = true;
            };
            img.src = STANDARD_FAVICON;
        }

        function renderFrame(scale: number) {
            if (!ctx || !imgReady) return null;
            ctx.clearRect(0, 0, 32, 32);
            ctx.save();
            ctx.translate(16, 16);
            ctx.scale(scale, scale);
            ctx.drawImage(img, -16, -16, 32, 32);
            ctx.restore();
            return canvas.toDataURL('image/png');
        }

        function startFaviconAnimation() {
            if (faviconIntervalId !== null) return;
            frameIndex = 0;

            faviconIntervalId = setInterval(() => {
                const faviconEl = getFaviconEl();
                if (!faviconEl) return;
                const scale = HEARTBEAT_FRAMES[frameIndex % HEARTBEAT_FRAMES.length];
                const dataUrl = renderFrame(scale);
                if (dataUrl) faviconEl.href = dataUrl;
                frameIndex++;
            }, 100);
        }

        function stopFaviconAnimation() {
            if (faviconIntervalId !== null) {
                clearInterval(faviconIntervalId);
                faviconIntervalId = null;
            }
            const faviconEl = getFaviconEl();
            if (faviconEl && originalHref) {
                faviconEl.href = originalHref;
            }
        }

        function startMarquee() {
            if (marqueeIntervalId !== null) return;
            let offset = 0;
            marqueeIntervalId = setInterval(() => {
                document.title = MARQUEE_TEXT.slice(offset) + MARQUEE_TEXT.slice(0, offset);
                offset = (offset + 1) % MARQUEE_TEXT.length;
            }, MARQUEE_STEP_MS);
        }

        function stopMarquee() {
            if (marqueeIntervalId !== null) {
                clearInterval(marqueeIntervalId);
                marqueeIntervalId = null;
            }
            document.title = STANDARD_TITLE;
        }

        function startFlashing() {
            if (titleIntervalId !== null) return;
            startFaviconAnimation();

            let step = 0;
            titleIntervalId = setInterval(() => {
                const isAlt = step % 2 === 0;
                document.title = isAlt
                    ? ALT_TITLES[Math.floor(step / 2) % ALT_TITLES.length]
                    : STANDARD_TITLE;
                step++;
            }, 3000);
        }

        function stopFlashing() {
            if (titleIntervalId !== null) {
                clearInterval(titleIntervalId);
                titleIntervalId = null;
            }
            stopFaviconAnimation();
            document.title = STANDARD_TITLE;
        }

        function handleVisibilityChange() {
            if (document.hidden) {
                stopMarquee();
                startFlashing();
            } else {
                stopFlashing();
                startMarquee();
            }
        }

        loadFaviconImage();
        if (!document.hidden) startMarquee();
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            stopFlashing();
            stopMarquee();
        };
    }, []);

    return null;
}
